import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { EXPORT_FORMATS, downloadReportExport, keys } from '../api/hooks'
import type { ExportFormat } from '../api/types'
import { problemOf } from '../api/client'

export const formatLabel: Record<ExportFormat, string> = { word: 'Word', excel: 'Excel', pdf: 'PDF', powerpoint: 'PowerPoint' }
const formatColor: Record<ExportFormat, string> = { word: '#2a5699', excel: '#1d6f42', pdf: '#c0392b', powerpoint: '#c55a11' }

/** Shared download state: which format is being generated and the last error (ProblemDetails title). */
function useReportDownload(reportId: string) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [busy, setBusy] = useState<ExportFormat | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function download(format: ExportFormat) {
    if (busy) return
    setBusy(format)
    setError(null)
    try {
      await downloadReportExport(reportId, format)
      void qc.invalidateQueries({ queryKey: keys.exports(reportId) })
    } catch (e) {
      const problem = problemOf(e)
      setError(problem.title ?? problem.detail ?? t('download.failed'))
    } finally {
      setBusy(null)
    }
  }
  return { busy, error, download, clearError: () => setError(null) }
}

/**
 * Report download in four formats.
 * - variant "group": a labelled button group for the report header.
 * - variant "menu": a compact "İndir ▾" dropdown for table rows (fixed-positioned so a scrolling table never clips it).
 */
export default function DownloadMenu({ reportId, variant = 'group' }: { reportId: string; variant?: 'group' | 'menu' }) {
  return variant === 'group' ? <DownloadGroup reportId={reportId} /> : <DownloadDropdown reportId={reportId} />
}

function DownloadGroup({ reportId }: { reportId: string }) {
  const { t } = useTranslation()
  const d = useReportDownload(reportId)
  return (
    <div className="download-group-wrap">
      <div className="download-group" role="group" aria-label={t('download.title')}>
        <span className="download-label">{t('download.title')}</span>
        {EXPORT_FORMATS.map((f) => (
          <button key={f} type="button" className="secondary" onClick={() => void d.download(f)} disabled={d.busy !== null} aria-busy={d.busy === f} title={t('download.as', { format: formatLabel[f] })}>
            <span className="format-dot" style={{ background: formatColor[f] }} />
            {d.busy === f ? t('download.preparing') : formatLabel[f]}
          </button>
        ))}
      </div>
      {d.busy ? <span className="muted small" role="status">{t('download.preparingHint', { format: formatLabel[d.busy] })}</span> : null}
      {d.error ? <span className="error small" role="alert">{d.error}</span> : null}
    </div>
  )
}

function DownloadDropdown({ reportId }: { reportId: string }) {
  const { t } = useTranslation()
  const d = useReportDownload(reportId)
  const [pos, setPos] = useState<CSSProperties | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const open = pos !== null

  useEffect(() => {
    if (!open) return
    const close = () => setPos(null)
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) close()
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [open])

  function toggle() {
    if (open) { setPos(null); return }
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    const width = 190
    const height = 210
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8))
    const below = rect.bottom + height < window.innerHeight
    d.clearError()
    setPos({ left, width, ...(below ? { top: rect.bottom + 4 } : { bottom: window.innerHeight - rect.top + 4 }) })
  }

  return (
    <span className="download-menu">
      <button ref={buttonRef} type="button" className="secondary small-btn" onClick={toggle} aria-haspopup="menu" aria-expanded={open} disabled={d.busy !== null}>
        {d.busy ? t('download.preparing') : <>{t('download.title')} ▾</>}
      </button>
      {d.error ? <span className="error small download-menu-error" role="alert">{d.error}</span> : null}
      {open ? (
        <div ref={menuRef} className="download-menu-list card" role="menu" style={pos ?? undefined}>
          {EXPORT_FORMATS.map((f) => (
            <button key={f} type="button" role="menuitem" className="nav-item" onClick={() => { setPos(null); void d.download(f) }}>
              <span className="row" style={{ gap: 8 }}><span className="format-dot" style={{ background: formatColor[f] }} />{formatLabel[f]}</span>
            </button>
          ))}
        </div>
      ) : null}
    </span>
  )
}
